import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * Protected route component for admin pages
 * Checks Supabase session AND admin_users table membership
 * Redirects to /admin-login if not authenticated or not an admin
 */
const ProtectedAdminRoute = ({ children }) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true = authenticated, false = not authenticated
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async () => {
      try {
        // ✅ ÉTAPE 1: Vérifier la session Supabase avec timeout
        const sessionPromise = supabase?.auth?.getSession();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Auth check timeout')), 8000)
        );

        let session;
        try {
          const { data, error: sessionError } = await Promise.race([sessionPromise, timeoutPromise]);
          if (sessionError || !data?.session) {
            if (!cancelled) {
              setIsAuthenticated(false);
              setIsAdmin(false);
            }
            return;
          }
          session = data?.session;
        } catch (timeoutErr) {
          console.warn('Session check timed out, redirecting to login');
          if (!cancelled) {
            setIsAuthenticated(false);
            setIsAdmin(false);
          }
          return;
        }

        // ✅ ÉTAPE 2: Vérifier que l'utilisateur existe dans admin_users avec timeout
        let adminUser = null;
        try {
          const adminQueryPromise = supabase
            ?.from('admin_users')
            ?.select('id, email, full_name')
            ?.eq('email', session?.user?.email)
            ?.single();

          const adminTimeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Admin query timeout')), 8000)
          );

          const { data: adminData, error: adminError } = await Promise.race([
            adminQueryPromise,
            adminTimeoutPromise,
          ]);

          if (!adminError) {
            adminUser = adminData;
          }
        } catch (adminErr) {
          console.warn('Admin users query failed or timed out:', adminErr?.message);
        }

        if (!adminUser) {
          // Utilisateur authentifié mais pas admin - déconnecter
          await supabase?.auth?.signOut();
          localStorage.removeItem('adtAdminAuth');
          sessionStorage.removeItem('adtAdminAuth');
          if (!cancelled) {
            setIsAuthenticated(false);
            setIsAdmin(false);
          }
          return;
        }

        // ✅ ÉTAPE 3: Session Supabase valide — pas besoin de vérifier sessionStorage
        // Clean up any legacy auth data
        localStorage.removeItem('adtAdminAuth');
        sessionStorage.removeItem('adtAdminAuth');

        // ✅ Tout est OK - utilisateur authentifié et admin
        if (!cancelled) {
          setIsAuthenticated(true);
          setIsAdmin(true);
        }

      } catch (error) {
        console.error('Auth check error:', error);
        await supabase?.auth?.signOut();
        localStorage.removeItem('adtAdminAuth');
        sessionStorage.removeItem('adtAdminAuth');
        if (!cancelled) {
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      }
    };

    checkAuth();

    // ✅ ÉTAPE 4: Écouter les changements d'état d'authentification
    const { data: authListener } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        localStorage.removeItem('adtAdminAuth');
        sessionStorage.removeItem('adtAdminAuth');
        if (!cancelled) {
          setIsAuthenticated(false);
          setIsAdmin(false);
        }
      } else if (event === 'SIGNED_IN') {
        // Revérifier l'appartenance à admin_users
        checkAuth();
      }
    });

    return () => {
      cancelled = true;
      authListener?.subscription?.unsubscribe();
    };
  }, []); // ✅ FIX: Remove location.pathname dependency to prevent excessive re-renders

  // Still checking authentication
  if (isAuthenticated === null || isAdmin === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Not authenticated or not admin - redirect to login
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/admin-login" replace state={{ from: location }} />;
  }

  // Authenticated AND admin - render protected content
  return children;
};

export default ProtectedAdminRoute;